import React from 'react';
import { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { Button, Row, Col } from 'reactstrap';
import validationSchema from './validationSchema';
import {
  callNativeAppRemoveLastRoute,
  HashimotoTestInputType,
  useTranslator,
} from '@front-activerse/common-components';
import { FormikInput as Input } from '@front-activerse/common-components';
import { formatIsoDate } from '@front-activerse/common-components';
import { formatIsoDateTime } from '@front-activerse/common-components';
import { useToast } from '@front-activerse/common-components';
import { callNativeMobileApp } from '@front-activerse/common-components';
import { useNavigate } from 'react-router-dom';

interface ITestsForm {
  addTestMeasurement: {
    addTest: (request: HashimotoTestInputType) => Promise<any>;
  };
  toggleModal?: () => void;
}

export const TestsForm: React.FC<ITestsForm> = ({ addTestMeasurement, toggleModal }) => {
  const { t } = useTranslator();
  const navigate = useNavigate();

  const initialValues = {
    tsh: '',
    ft3: '',
    ft4: '',
    antiTg: '',
    antiTpo: '',
    testedAt: formatIsoDate(new Date()),
  };

  const { toastError, toastSuccess } = useToast();

  useEffect(() => {
    // trackFirebaseEvent('screen_view', {
    //   firebase_screen: 'Lab_results_add',
    //   lab_results_add: 'hashimoto',
    // });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: typeof initialValues) => {
    const time = new Date().toLocaleTimeString('pl-Pl', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });

    const request: HashimotoTestInputType = {
      tsh: values.tsh === '' ? undefined : +values.tsh,
      ft3: values.ft3 === '' ? undefined : +values.ft3,
      ft4: values.ft4 === '' ? undefined : +values.ft4,
      antiTg: values.antiTg === '' ? undefined : +values.antiTg,
      antiTpo: values.antiTpo === '' ? undefined : +values.antiTpo,
      testedAt: formatIsoDateTime(new Date(values.testedAt).toISOString().slice(0, 10), time),
    };

    try {
      const response = await addTestMeasurement.addTest(request);

      if (response.data.hashimoto.hashimotoTest.code === 200) {
        toastSuccess(
          <p className="mb-0 text-center">{t('labTests.notification.thyroidEntrySaved')}</p>
        );
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'tests',
          },
        });
        // close webview only if TestsForm is rendered in Add Page and toggleModal is undefined
        if (!toggleModal) {
          callNativeMobileApp({
            action: 'close_modal',
          });
        }
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      navigate(-1);
      callNativeAppRemoveLastRoute();
    }
  };

  return (
    <Formik
      validationSchema={() => validationSchema(t)}
      validateOnChange
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ values, isValid, isSubmitting }) => {
        return (
          <Form>
            <Input name="tsh" type="number" label="TSH" unit="mIU/l" step="0.001" />
            <Input name="ft3" type="number" label="FT3" unit="pmol/l" step="0.01" />
            <Input name="ft4" type="number" label="FT4" unit="pmol/l" step="0.01" />
            <Input name="antiTg" type="number" label="anty-TG" unit="IU/ml" step="0.001" />
            <Input name="antiTpo" type="number" label="anty-TPO" unit="IU/ml" step="0.001" />
            <Input name="testedAt" type="date" label="Data badań" max={formatIsoDate(new Date())} />

            <Row className="mt-xl">
              <Col xs="6" className="pe-1">
                <Button
                  color="secondary"
                  outline
                  className="w-100"
                  onClick={() => {
                    navigate(-1);
                    callNativeAppRemoveLastRoute();
                  }}
                >
                  {t('labTests.cancelButton')}
                </Button>
              </Col>
              <Col xs="6" className="ps-1">
                <Button
                  color="primary"
                  type="submit"
                  className="w-100"
                  disabled={
                    (values.tsh === '' &&
                      values.ft3 === '' &&
                      values.ft4 === '' &&
                      values.antiTg === '' &&
                      values.antiTpo === '') ||
                    !isValid ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>{t('labTests.saveButton')}</span>
                    </>
                  ) : (
                    t('labTests.saveButton')
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

export default TestsForm;
